/**
 * 机构分时股池 - 常量、类型与接口数据映射
 * courseNo: 1c261467f05747f2beb3115d26a2b98c
 */

import moment from 'moment'
import type { TableColumn } from './courseConfig'
import { QUANT_GOLD_COURSE_NO, getQuantGoldProductFallback } from './quantGoldMock'

export const AGENCY_INTRADAY_COURSE_NO = '1c261467f05747f2beb3115d26a2b98c'

/** 机构/散户买卖列固定色（PRD） */
export const AGENCY_RATE_COLORS = {
  mainInRate: 'rgba(254, 64, 67, 1)',
  mainOutRate: 'rgba(38, 177, 115, 1)',
  retailInRate: 'rgba(158, 84, 255, 1)',
  retailOutRate: 'rgba(38, 189, 255, 1)'
} as const

export type AgencyRateColorKey = keyof typeof AGENCY_RATE_COLORS

export function getAgencyRateColor(key: string): string | undefined {
  return AGENCY_RATE_COLORS[key as AgencyRateColorKey]
}

export type AgencyMainTab = 'allMarket' | 'optional'
export type AgencySubTab = 'agency' | 'retail'

export interface AgencyRangeFilter {
  buyMinIn: string
  buyMaxIn: string
  sellMinIn: string
  sellMaxIn: string
}

export interface AgencyIntradayFilters {
  agency: AgencyRangeFilter
  retail: AgencyRangeFilter
  popupType: number
  plateHot: number
}

/** 单个二级 Tab（看机构/看散户）的筛选条件 */
export interface AgencySubTabFilters {
  range: AgencyRangeFilter
  popupType: number
  plateHot: number
}

export interface AgencySubTabFilterChangePayload {
  subTab: AgencySubTab
  filters: AgencySubTabFilters
}

export interface AgencyPoolItem {
  symbol: string
  market: string
  name: string
  lastPx: number | null
  pxChangRate: number | null
  preClosePx?: number | null
  mainInRate: number | null
  mainOutRate: number | null
  retailInRate: number | null
  retailOutRate: number | null
  goldFlag: number
  plateName: string
  isPermission: number
  updateTime?: number
  _agencyGoldDisplay?: 'trigger' | 'view' | 'empty'
  _agencyGoldPinned?: boolean
}

export const AGENCY_RATE_MIN = 0
export const AGENCY_RATE_MAX = 100

export const AGENCY_MARKET_TYPE_OPTIONS = [
  { value: 0, label: '全部A股' },
  { value: 1, label: '沪深主板' },
  { value: 2, label: '上证A股' },
  { value: 3, label: '深证A股' },
  { value: 4, label: '创业板' },
  { value: 5, label: '科创板' }
]

export const AGENCY_PLATE_HOT_OPTIONS = [
  { value: 0, label: '全部' },
  { value: 10, label: '热度Top10' },
  { value: 20, label: '热度Top20' },
  { value: 50, label: '热度Top50' }
]

export interface AgencyFilterOption {
  value: number
  label: string
}

export interface AgencyPopupConfig {
  marketTypeOptions: AgencyFilterOption[]
  plateHotOptions: AgencyFilterOption[]
  defaultFilters: AgencyIntradayFilters
}

function formatRateDefault(val: number | null | undefined, fallback: number): string {
  if (val == null || val === '' as any) return String(fallback)
  const num = Number(val)
  if (!Number.isFinite(num)) return String(fallback)
  const percent = Math.abs(num) <= 1 ? num * 100 : num
  return percent.toFixed(2)
}

function applyAgencyRangeItem(
  filters: AgencyIntradayFilters,
  name: string,
  min: number | null | undefined,
  max: number | null | undefined
) {
  const minStr = formatRateDefault(min, AGENCY_RATE_MIN)
  const maxStr = formatRateDefault(max, AGENCY_RATE_MAX)
  if (name.includes('机构') && name.includes('买')) {
    filters.agency.buyMinIn = minStr
    filters.agency.buyMaxIn = maxStr
  } else if (name.includes('机构') && name.includes('卖')) {
    filters.agency.sellMinIn = minStr
    filters.agency.sellMaxIn = maxStr
  } else if (name.includes('散户') && name.includes('买')) {
    filters.retail.buyMinIn = minStr
    filters.retail.buyMaxIn = maxStr
  } else if (name.includes('散户') && name.includes('卖')) {
    filters.retail.sellMinIn = minStr
    filters.retail.sellMaxIn = maxStr
  }
}

function sortAgencyFilterOptionsById(options: AgencyFilterOption[]): AgencyFilterOption[] {
  return [...options].sort((a, b) => a.value - b.value)
}

export function parseAgencyMarketCodePopup(res: any): AgencyFilterOption[] {
  const layer1 = res?.data != null ? res.data : res
  const list = Array.isArray(layer1)
    ? layer1
    : (Array.isArray(layer1?.data) ? layer1.data : [])
  if (!list.length) return AGENCY_MARKET_TYPE_OPTIONS.map(item => ({ ...item }))
  return sortAgencyFilterOptionsById(list.map((item: any) => ({
    value: Number(item.id ?? 0),
    label: String(item.name ?? '')
  })))
}

export function parseAgencyPlateHotOptions(plate: any): AgencyFilterOption[] {
  if (Array.isArray(plate)) {
    const options = sortAgencyFilterOptionsById(plate.map((item: any) => ({
      value: Number(item.id ?? item.key ?? 0),
      label: String(item.name ?? item.value ?? '')
    })))
    return options.length ? options : AGENCY_PLATE_HOT_OPTIONS.map(item => ({ ...item }))
  }
  if (plate && typeof plate === 'object') {
    const options = sortAgencyFilterOptionsById(Object.entries(plate).map(([key, val]) => ({
      value: Number(key),
      label: typeof val === 'string' ? val : String((val as any)?.name ?? val)
    })))
    return options.length ? options : AGENCY_PLATE_HOT_OPTIONS.map(item => ({ ...item }))
  }
  return AGENCY_PLATE_HOT_OPTIONS.map(item => ({ ...item }))
}

export function parseAgencyMarketPopup(res: any): {
  defaultFilters: AgencyIntradayFilters
  plateHotOptions: AgencyFilterOption[]
} {
  const layer1 = res?.data != null ? res.data : res
  const payload = layer1?.agency != null || layer1?.plate != null ? layer1 : layer1?.data
  const defaultFilters = createDefaultAgencyFilters()
  const agencyList = Array.isArray(payload?.agency) ? payload.agency : []
  agencyList.forEach((item: any) => {
    applyAgencyRangeItem(defaultFilters, String(item?.name ?? ''), item?.min, item?.max)
  })
  const plateHotOptions = parseAgencyPlateHotOptions(payload?.plate)
  const defaultPlateHot = plateHotOptions[0]?.value ?? 0
  defaultFilters.plateHot = defaultPlateHot
  return { defaultFilters, plateHotOptions }
}

export function buildAgencyPopupConfig(marketCodeRes: any, marketRes: any): AgencyPopupConfig {
  const marketTypeOptions = parseAgencyMarketCodePopup(marketCodeRes)
  const { defaultFilters, plateHotOptions } = parseAgencyMarketPopup(marketRes)
  defaultFilters.popupType = marketTypeOptions[0]?.value ?? 0
  return {
    marketTypeOptions,
    plateHotOptions,
    defaultFilters
  }
}

export function createFallbackAgencyPopupConfig(): AgencyPopupConfig {
  return {
    marketTypeOptions: AGENCY_MARKET_TYPE_OPTIONS.map(item => ({ ...item })),
    plateHotOptions: AGENCY_PLATE_HOT_OPTIONS.map(item => ({ ...item })),
    defaultFilters: createDefaultAgencyFilters()
  }
}

const INDEX_POOL_ASSET_BASE = 'https://pcstatic.sylapp.cn/helpmanual/assets/indexPool/1c261467f05747f2beb3115d26a2b98c'

export const AGENCY_INTRADAY_PRODUCT_FALLBACK = {
  courseNo: AGENCY_INTRADAY_COURSE_NO,
  poolName: '机构分时',
  poolNameImg: `${INDEX_POOL_ASSET_BASE}/poolNameImg.png`,
  poolNameImgDark: `${INDEX_POOL_ASSET_BASE}/poolNameImgDark.png`,
  remark: '*数据来源于交易所和第三方数据库，所涉及任何内容及操作仅供您参考和学习使用，不构成投资建议。投资有风险，入市须谨慎。',
  tags: ['低位潜力', '强势龙头', '二波启动'],
  content: '筛选不同阶段龙头标的，把握龙头股全生命周期交易机会',
  disclaimer: '',
  poolIcon: `${INDEX_POOL_ASSET_BASE}/poolIcon.png`,
  historyDays: '10'
}

export function getAgencyIntradayProductFallback() {
  return [{ ...AGENCY_INTRADAY_PRODUCT_FALLBACK }]
}

export function createDefaultAgencyRangeFilter(): AgencyRangeFilter {
  return {
    buyMinIn: String(AGENCY_RATE_MIN),
    buyMaxIn: String(AGENCY_RATE_MAX),
    sellMinIn: String(AGENCY_RATE_MIN),
    sellMaxIn: String(AGENCY_RATE_MAX)
  }
}

export function createDefaultAgencyFilters(): AgencyIntradayFilters {
  return {
    agency: createDefaultAgencyRangeFilter(),
    retail: createDefaultAgencyRangeFilter(),
    popupType: 0,
    plateHot: 0
  }
}

export function cloneAgencySubTabFilters(filters: AgencySubTabFilters): AgencySubTabFilters {
  return {
    range: { ...filters.range },
    popupType: filters.popupType,
    plateHot: filters.plateHot
  }
}

export function createDefaultAgencySubTabFilters(
  popupType = 0,
  plateHot = 0
): AgencySubTabFilters {
  return {
    range: createDefaultAgencyRangeFilter(),
    popupType,
    plateHot
  }
}

/** 从弹窗接口默认值生成单个二级 Tab 的默认筛选 */
export function getAgencySubTabDefaultFilters(
  defaults: AgencyIntradayFilters,
  subTab: AgencySubTab,
  marketPopupType?: number
): AgencySubTabFilters {
  return {
    range: {
      ...(subTab === 'agency' ? defaults.agency : defaults.retail)
    },
    popupType: marketPopupType ?? defaults.popupType ?? 0,
    plateHot: defaults.plateHot ?? 0
  }
}

export function isSameAgencySubTabFilters(a: AgencySubTabFilters, b: AgencySubTabFilters): boolean {
  return (
    isSameAgencyRangeFilter(a.range, b.range) &&
    a.popupType === b.popupType &&
    a.plateHot === b.plateHot
  )
}

function normalizeMarket(market: string | number | undefined, symbol?: string): string {
  const m = String(market ?? '').toLowerCase().trim()
  if (m === 'sh' || m === 'sse' || m === '1' || m === 'sha' || m === 'shetf') return 'sh'
  if (m === 'sz' || m === 'szse' || m === '0' || m === 'sza' || m === 'szetf') return 'sz'
  if (m === 'bj' || m === 'bse' || m === '2') return 'bj'
  if (symbol) return inferMarketFromSymbol(String(symbol))
  return 'sz'
}

function inferMarketFromSymbol(symbol: string): string {
  const code = String(symbol || '')
  if (code.startsWith('6') || code.startsWith('5')) return 'sh'
  if (code.startsWith('0') || code.startsWith('3') || code.startsWith('1')) return 'sz'
  if (code.startsWith('4') || code.startsWith('8')) return 'bj'
  return 'sz'
}

function resolveAgencyLastPx(item: any, pxChangRate: number | null): number | null {
  const candidates = [item.lastPx, item.lastPrice, item.LastPx, item.closePx, item.ClosePx, item.price]
  for (const raw of candidates) {
    if (raw != null && raw !== '' && Number(raw) !== 0) {
      return Number(raw)
    }
  }
  const preCloseRaw = item.preClosePx ?? item.PreClosePx ?? item.preClose ?? item.yclose
  if (pxChangRate != null && preCloseRaw != null && preCloseRaw !== '') {
    const preClose = Number(preCloseRaw)
    if (Number.isFinite(preClose) && preClose > 0) {
      return Math.round(preClose * (1 + pxChangRate) * 100) / 100
    }
  }
  return null
}

/** 机构分时信号池去重 key：symbol + market */
export function getAgencyPoolDedupKey(item: {
  symbol?: string | number
  market?: string | number
  Market?: string
  exchange?: string
}): string {
  const symbolRaw = item.symbol
  if (symbolRaw == null || symbolRaw === '') return ''
  const symbol = String(symbolRaw).padStart(6, '0')
  const market = normalizeMarket(item.market || item.Market || item.exchange || '', symbol)
  return `${symbol}_${market}`
}

/** 接口 pool 列表按 symbol+market 去重，保留首次出现 */
export function dedupeAgencyRawPoolList(rawList: any[]): any[] {
  if (!Array.isArray(rawList) || !rawList.length) return []
  const seen = new Set<string>()
  const result: any[] = []
  for (const item of rawList) {
    const key = getAgencyPoolDedupKey(item)
    if (!key || seen.has(key)) continue
    seen.add(key)
    result.push(item)
  }
  return result
}

/** 已映射列表去重，可排除已有列表中的股票（加载更多场景） */
export function dedupeAgencyPoolItems(
  items: AgencyPoolItem[],
  existingKeys?: Set<string>
): AgencyPoolItem[] {
  const seen = existingKeys ? new Set(existingKeys) : new Set<string>()
  const result: AgencyPoolItem[] = []
  for (const item of items) {
    const key = getAgencyPoolDedupKey(item)
    if (!key || seen.has(key)) continue
    seen.add(key)
    result.push(item)
  }
  return result
}

export function mapAgencyPoolItem(item: any): AgencyPoolItem {
  const symbol = String(item.symbol || '')
  const market = normalizeMarket(item.market || item.Market || item.exchange || '', symbol)
  const pxChangRateRaw = item.pxChangeRate ?? item.pxChangRate
  const pxChangRate = pxChangRateRaw != null && pxChangRateRaw !== '' ? Number(pxChangRateRaw) : null
  return {
    symbol,
    market,
    name: item.name || '',
    lastPx: resolveAgencyLastPx(item, pxChangRate),
    pxChangRate,
    preClosePx: item.preClosePx ?? item.preClose ?? item.PreClosePx ?? null,
    mainInRate: item.mainInRate ?? null,
    mainOutRate: item.mainOutRate ?? null,
    retailInRate: item.retailInRate ?? null,
    retailOutRate: item.retailOutRate ?? null,
    goldFlag: Number(item.goldFlag ?? 0),
    plateName: item.plateName || '',
    // isPermission: 1=有量化淘金权限，0=无权限
    isPermission: Number(item.isPermission ?? 0),
    updateTime: item.updateTime
  }
}

/** 解析量化权限：接口 isPermission 1=有权限，0=无权限（支持 data 层级或 pool 项内返回） */
export function resolveAgencyQuantPermission(payload: any, rawList: any[] = []): boolean {
  if (payload?.isPermission != null && payload.isPermission !== '') {
    return Number(payload.isPermission) === 1
  }
  const itemWithFlag = rawList.find(item => item?.isPermission != null && item.isPermission !== '')
  if (itemWithFlag) {
    return Number(itemWithFlag.isPermission) === 1
  }
  return false
}

function parseRateInput(val: string): number | undefined {
  const trimmed = String(val ?? '').trim()
  if (trimmed === '') return undefined
  const num = Number(trimmed)
  if (!Number.isFinite(num)) return undefined
  return Math.round(num * 100) / 100
}

/** 筛选框百分比(0-100) → 接口小数(0-1) */
function toApiRate(val: string): number | undefined {
  const percent = parseRateInput(val)
  if (percent == null) return undefined
  return Math.round((percent / 100) * 10000) / 10000
}

function buildRangePayload(range: AgencyRangeFilter) {
  const payload: Record<string, number> = {}
  const buyMin = toApiRate(range.buyMinIn)
  const buyMax = toApiRate(range.buyMaxIn)
  const sellMin = toApiRate(range.sellMinIn)
  const sellMax = toApiRate(range.sellMaxIn)
  if (buyMin != null) payload.buyMinIn = buyMin
  if (buyMax != null) payload.buyMaxIn = buyMax
  if (sellMin != null) payload.sellMinIn = sellMin
  if (sellMax != null) payload.sellMaxIn = sellMax
  return payload
}

function normalizeRangePercent(val: string, fallback: number): number {
  const parsed = parseRateInput(val)
  return parsed ?? fallback
}

/** 将筛选输入钳制在接口返回的买卖区间范围内 */
export function clampAgencyRangeInput(
  field: keyof AgencyRangeFilter,
  rawValue: string,
  defaultRange: AgencyRangeFilter
): string {
  const isBuy = field === 'buyMinIn' || field === 'buyMaxIn'
  const boundMin = normalizeRangePercent(
    isBuy ? defaultRange.buyMinIn : defaultRange.sellMinIn,
    AGENCY_RATE_MIN
  )
  const boundMax = normalizeRangePercent(
    isBuy ? defaultRange.buyMaxIn : defaultRange.sellMaxIn,
    AGENCY_RATE_MAX
  )

  const trimmed = String(rawValue ?? '').trim()
  if (trimmed === '') {
    return (field.includes('Min') ? boundMin : boundMax).toFixed(2)
  }

  const num = Number(trimmed)
  if (!Number.isFinite(num)) {
    return (field.includes('Min') ? boundMin : boundMax).toFixed(2)
  }

  const clamped = Math.min(Math.max(Math.round(num * 100) / 100, boundMin), boundMax)
  return clamped.toFixed(2)
}

/** 确保买卖区间最小值不大于最大值 */
export function normalizeAgencyRangePair(range: AgencyRangeFilter) {
  const buyMin = parseRateInput(range.buyMinIn)
  const buyMax = parseRateInput(range.buyMaxIn)
  if (buyMin != null && buyMax != null && buyMin > buyMax) {
    range.buyMinIn = buyMax.toFixed(2)
    range.buyMaxIn = buyMin.toFixed(2)
  }
  const sellMin = parseRateInput(range.sellMinIn)
  const sellMax = parseRateInput(range.sellMaxIn)
  if (sellMin != null && sellMax != null && sellMin > sellMax) {
    range.sellMinIn = sellMax.toFixed(2)
    range.sellMaxIn = sellMin.toFixed(2)
  }
}

/** 买卖区间是否与默认筛选一致（默认不传 agency/retail 参数） */
export function isSameAgencyRangeFilter(a: AgencyRangeFilter, b: AgencyRangeFilter): boolean {
  return (
    normalizeRangePercent(a.buyMinIn, AGENCY_RATE_MIN) === normalizeRangePercent(b.buyMinIn, AGENCY_RATE_MIN) &&
    normalizeRangePercent(a.buyMaxIn, AGENCY_RATE_MAX) === normalizeRangePercent(b.buyMaxIn, AGENCY_RATE_MAX) &&
    normalizeRangePercent(a.sellMinIn, AGENCY_RATE_MIN) === normalizeRangePercent(b.sellMinIn, AGENCY_RATE_MIN) &&
    normalizeRangePercent(a.sellMaxIn, AGENCY_RATE_MAX) === normalizeRangePercent(b.sellMaxIn, AGENCY_RATE_MAX)
  )
}

export function buildAgencyPoolQuery(options: {
  mainTab: AgencyMainTab
  subTab: AgencySubTab
  pageNo: number
  pageSize: number
  sortField: string
  sortType: string
  filters: AgencySubTabFilters
  sortFieldMap: Record<string, string>
  /** 当前 Tab 已在筛选弹窗点「确定」后才传筛选参数 */
  applyFilters?: boolean
  /** 当前 Tab 买卖区间默认值，与之一致时不传 agency/retail */
  defaultRange?: AgencyRangeFilter
  userName?: string
}) {
  const {
    mainTab,
    subTab,
    pageNo,
    pageSize,
    sortField,
    sortType,
    filters,
    sortFieldMap,
    applyFilters = false,
    defaultRange,
    userName
  } = options

  const query: Record<string, any> = {
    isOptional: mainTab === 'optional' ? 1 : 0,
    pageNo,
    pageSize
  }

  if (mainTab !== 'optional') {
    query.isAgency = subTab === 'agency' ? 1 : 2
  }

  if (sortField && sortFieldMap[sortField]) {
    query.sortField = sortFieldMap[sortField]
  }
  if (sortType) {
    query.sortType = sortType === 'ascend' || sortType === 'asc' ? 'asc' : 'desc'
  }

  // 看自选不支持筛选；未点「确定」时不传任何筛选参数
  if (mainTab !== 'optional' && applyFilters) {
    if (filters.popupType != null && filters.popupType !== 0) {
      query.marketType = { popupType: filters.popupType }
    }

    if (filters.plateHot != null && filters.plateHot !== 0) {
      query.plateType = { plateHot: filters.plateHot }
    }

    const rangeBaseline = defaultRange ?? createDefaultAgencyRangeFilter()
    if (!isSameAgencyRangeFilter(filters.range, rangeBaseline)) {
      const rangePayload = buildRangePayload(filters.range)
      if (Object.keys(rangePayload).length > 0) {
        if (subTab === 'agency') {
          query.agency = rangePayload
        } else {
          query.retail = rangePayload
        }
      }
    }
  }

  if (userName) {
    query.userName = userName
  }

  return query
}

export function formatAgencyRate(val: number | null | undefined): string {
  if (val === null || val === undefined) return '--'
  const percent = Math.abs(val) <= 1 ? val * 100 : val
  return percent.toFixed(2) + '%'
}

/** 机构分时最新价展示：优先 lastPx，其次昨收+涨幅推算 */
export function resolveAgencyDisplayLastPx(record: {
  lastPx?: number | null
  pxChangRate?: number | null
  preClosePx?: number | null
}): number | null {
  if (record.lastPx != null && record.lastPx !== 0) {
    return Number(record.lastPx)
  }
  const preCloseRaw = record.preClosePx
  const rateRaw = record.pxChangRate
  if (preCloseRaw != null && rateRaw != null) {
    const preClose = Number(preCloseRaw)
    const rate = Number(rateRaw)
    if (Number.isFinite(preClose) && preClose > 0 && Number.isFinite(rate)) {
      return Math.round(preClose * (1 + rate) * 100) / 100
    }
  }
  return null
}

export function formatAgencyUpdateTime(updateTime: number | string | null | undefined): string {
  if (updateTime == null || updateTime === '') return ''
  const n = Number(updateTime)
  if (Number.isFinite(n) && n > 0) {
    const ms = n > 9999999999 ? n : n * 1000
    return moment(ms).format('YYYY-MM-DD HH:mm:ss')
  }
  const parsed = moment(updateTime)
  if (parsed.isValid()) return parsed.format('YYYY-MM-DD HH:mm:ss')
  return String(updateTime)
}

/** 解析机构分时接口 data 层 */
export function extractAgencyPayload(res: any) {
  if (!res) return {}
  const layer1 = res.data != null ? res.data : res
  if (layer1?.pool != null || layer1?.updateTime != null) return layer1
  if (layer1?.data?.pool != null || layer1?.data?.updateTime != null) return layer1.data
  return layer1
}

export function getAgencyGoldColumnTitle(hasPermission: boolean): string {
  return hasPermission ? '淘金指' : '量化信号'
 
}

/** 跳转指标名：淘金指（量化淘金产品 poolName） */
export function resolveAgencyGoldIndicatorAlias(productList: any[] = []): string {
  const quantGoldProduct = productList.find(item => item?.courseNo === QUANT_GOLD_COURSE_NO)
  if (quantGoldProduct?.poolName) {
    return String(quantGoldProduct.poolName)
  }
  return getQuantGoldProductFallback()[0]?.poolName || ''
}

/** 跳转指标名：机构分时（当前产品 poolName） */
export function resolveAgencyIntradayIndicatorAlias(productList: any[] = []): string {
  const agencyProduct = productList.find(item => item?.courseNo === AGENCY_INTRADAY_COURSE_NO)
  if (agencyProduct?.poolName) {
    return String(agencyProduct.poolName)
  }
  return AGENCY_INTRADAY_PRODUCT_FALLBACK.poolName || ''
}

/** @deprecated 使用 resolveAgencyGoldIndicatorAlias */
export function resolveAgencyIndicatorAlias(productList: any[] = []): string {
  return resolveAgencyGoldIndicatorAlias(productList)
}

function buildReorderedStockListGw(
  list: Array<{ symbol: string; market?: string }>,
  focus: { symbol: string; market?: string }
): string {
  const clickedIndex = list.findIndex(item => item.symbol === focus.symbol)
  const reordered = clickedIndex >= 0
    ? [list[clickedIndex], ...list.slice(clickedIndex + 1), ...list.slice(0, clickedIndex)]
    : [focus, ...list]
  return reordered
    .map(item => `${String(item.market || 'sz').toLowerCase()}:${item.symbol}`)
    .join(',')
}

/** 机构分时：行双击 / 搜索下拉双击 → 分时图 + 机构分时指标 */
export function openAgencyIndicatorNavigation(
  record: { symbol: string; market?: string },
  poolList: Array<{ symbol: string; market?: string }> = [],
  productList: any[] = []
) {
  if (!record?.symbol) return
  const code = record.symbol
  const market = String(record.market || 'sz').toLowerCase()
  const stockList = buildReorderedStockListGw(poolList, record)
  const indAlias = resolveAgencyIntradayIndicatorAlias(productList)
  if (!indAlias) return

  window.open(`gw://funccall?funcname=SetCurStockList;list=${stockList}`)
  setTimeout(() => {
    const url2 = `gw://funccall?funcname=OnFireCommand;setcurrent=1;Command=fszs;param=<params><code>${code}</code><market_letter>${market}</market_letter><technique_type>0</technique_type><technique_name></technique_name><element_InfoTab><SubTabName></SubTabName></element_InfoTab><QUOTE_TAB_pt><SubTabName></SubTabName></QUOTE_TAB_pt><level2_orderchart_type>-1</level2_orderchart_type><StyleName>FSZS</StyleName><TabName>分时图</TabName><flag_container_style>R</flag_container_style><ind_alias>${indAlias}</ind_alias></params>`
    window.open(url2)
  }, 100)
}

/** 机构分时：淘金指/量化信号列双击 → K 线图 + 淘金指指标 */
export function openAgencyGoldKlineNavigation(
  record: { symbol: string; market?: string },
  poolList: Array<{ symbol: string; market?: string }> = [],
  productList: any[] = []
) {
  if (!record?.symbol) return
  const code = record.symbol
  const market = String(record.market || 'sz').toLowerCase()
  const stockList = buildReorderedStockListGw(poolList, record)
  const indAlias = resolveAgencyGoldIndicatorAlias(productList)
  if (!indAlias) return

  window.open(`gw://funccall?funcname=SetCurStockList;list=${stockList}`)
  setTimeout(() => {
    const url2 = `gw://funccall?funcname=OnFireCommand;setcurrent=1;Command=kline;param=<params><kline_special_event>0</kline_special_event><StyleName>JSFX</StyleName><code>${code}</code><market_letter>${market}</market_letter><periodstr>DAY</periodstr><TabName>K线图</TabName><flag_container_style>K</flag_container_style><ind_alias>${indAlias}</ind_alias></params>`
    window.open(url2)
  }, 100)
}

export function resolveAgencyGoldDisplay(
  item: AgencyPoolItem,
  hasPermission: boolean,
  pinnedSymbols: Set<string>,
  isOptionalTab: boolean
): 'trigger' | 'view' | 'empty' {
  const hasSignal = item.goldFlag != null && item.goldFlag !== 0

  if (hasPermission) {
    return hasSignal ? 'trigger' : 'empty'
  }

  if (isOptionalTab) {
    if (pinnedSymbols.size === 0) return 'view'
    return pinnedSymbols.has(item.symbol) && hasSignal ? 'trigger' : 'view'
  }

  return pinnedSymbols.has(item.symbol) && hasSignal ? 'trigger' : 'view'
}

export function computeAgencyPinnedGoldSymbols(
  list: AgencyPoolItem[],
  hasPermission: boolean
): Set<string> {
  if (hasPermission) return new Set()
  const symbols = list
    .filter(item => item.goldFlag != null && item.goldFlag !== 0)
    .slice(0, 2)
    .map(item => item.symbol)
  return new Set(symbols)
}

export function applyAgencyGoldDisplayAndSort(
  list: AgencyPoolItem[],
  hasPermission: boolean,
  pinnedSymbols: Set<string>,
  isOptionalTab: boolean
): AgencyPoolItem[] {
  const mapped = list.map(item => {
    const display = resolveAgencyGoldDisplay(item, hasPermission, pinnedSymbols, isOptionalTab)
    return {
      ...item,
      _agencyGoldDisplay: display,
      _agencyGoldPinned: !hasPermission && pinnedSymbols.has(item.symbol)
    }
  })

  if (hasPermission) return mapped

  const pinned = mapped.filter(item => item._agencyGoldPinned && item._agencyGoldDisplay === 'trigger')
  const rest = mapped.filter(item => !(item._agencyGoldPinned && item._agencyGoldDisplay === 'trigger'))
  return [...pinned, ...rest]
}

export function getAgencyVisibleColumnKeys(
  mainTab: AgencyMainTab,
  subTab: AgencySubTab,
  hasPermission = true
): Set<string> {
  const keys = new Set(['index', 'symbol', 'name', 'lastPx', 'pxChangRate', 'plateName'])
  if (hasPermission) {
    keys.add('goldSignal')
  }

  if (mainTab === 'optional') {
    keys.add('mainInRate')
    keys.add('mainOutRate')
    keys.add('retailInRate')
    keys.add('retailOutRate')
    return keys
  }

  if (subTab === 'agency') {
    keys.add('mainInRate')
    keys.add('mainOutRate')
  } else {
    keys.add('retailInRate')
    keys.add('retailOutRate')
  }

  return keys
}

export function filterAgencyColumns(
  columns: TableColumn[],
  mainTab: AgencyMainTab,
  subTab: AgencySubTab,
  hasPermission = true
): TableColumn[] {
  const visible = getAgencyVisibleColumnKeys(mainTab, subTab, hasPermission)
  return columns.filter(col => visible.has(col.key))
}

export { QUANT_GOLD_COURSE_NO }
